import emailjs from '@emailjs/browser';

// Use the same EmailJS initialization as in appointmentFeedbackService
// Service is already initialized in appointmentFeedbackService.js

class VerificationService {
  constructor() {
    // Store OTP data with expiration times
    this.otpStore = new Map();
    // OTP expiration time in milliseconds (10 minutes)
    this.otpExpirationMs = 10 * 60 * 1000;
  }

  /**
   * Send a verification OTP code via email
   * @param {string} email - Recipient email address
   * @param {string} name - Recipient name
   * @param {string} otp - The OTP code to send
   * @returns {Promise<Object>} - Result of the email send operation
   */
  async sendVerificationOTP(email, name, otp) {
    try {
      console.log(`Sending OTP ${otp} to ${name} (${email})`);
      
      // Store OTP with expiration time
      const expirationTime = Date.now() + this.otpExpirationMs;
      this.storeOTP(email, otp, expirationTime);
      
      // Send email using emailjs - using the same service ID as feedback service
      // but with a different template that's specific for OTP verification
      const result = await emailjs.send(
        'tebra', // Service ID - same as in appointmentFeedbackService
        'template_wcqkz57', // Template ID - should be changed to an OTP verification template
        {
          to_email: email,
          to_name: name,
          subject: 'Patient Verification',
          otp: otp, // Changed from 'message' to 'otp'
          expiry_time: '10 minutes',
        }
      );
      
      console.log(`Verification email sent successfully to ${email}`, result);
      return { success: true, result };
    } catch (error) {
      console.error('Error sending verification email:', error);
      throw new Error('Failed to send verification email: ' + error.message);
    }
  }
  
  /**
   * Generate a 6-digit OTP code
   * @returns {string} - Generated OTP code
   */
  generateOTP() {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  /**
   * Store OTP in memory with expiration time
   * @param {string} email - User email
   * @param {string} otp - Generated OTP
   * @param {number} expirationTime - Timestamp when OTP expires
   */
  storeOTP(email, otp, expirationTime) {
    this.otpStore.set(email, { otp, expirationTime });
    
    // Set up automatic cleanup of expired OTP
    setTimeout(() => {
      const otpData = this.otpStore.get(email);
      if (otpData && otpData.otp === otp) {
        this.otpStore.delete(email);
        console.log(`OTP for ${email} expired and removed from store`);
      }
    }, this.otpExpirationMs);
  }

  /**
   * Verify an OTP code
   * @param {string} email - User email
   * @param {string} otp - OTP to verify
   * @returns {Object} - Verification result with success and message
   */
  verifyOTP(email, otp) {
    const otpData = this.otpStore.get(email);
    
    // Check if OTP exists
    if (!otpData) {
      return { 
        success: false, 
        message: 'Verification code not found. Please request a new code.' 
      };
    }
    
    // Check if OTP is expired
    if (Date.now() > otpData.expirationTime) {
      this.otpStore.delete(email);
      return { 
        success: false, 
        message: 'Verification code has expired. Please request a new code.' 
      };
    }
    
    // Check if OTP matches
    if (otpData.otp !== otp) {
      return { 
        success: false, 
        message: 'Invalid verification code. Please try again.' 
      };
    }
    
    // OTP is valid - remove it from store to prevent reuse
    this.otpStore.delete(email);
    
    return { 
      success: true, 
      message: 'Verification successful!' 
    };
  }
}

export default new VerificationService();
